import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TooltipProvider,
} from '@roger/ui-components';
import { Loader2, Pencil, UserPlus } from 'lucide-react';
import { listRoles } from '../../services/api';
import type { RoleResponse } from '../../services/api/types.gen';
import { PermissionBadge } from './PermissionBadge';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import CreateRoleButton from './CreateRoleButton';
import EditRoleDialog from './EditRoleDialog';
import BulkAssignRoleDialog from './BulkAssignRoleDialog';

const RolesTable: React.FC = () => {
  const { showError } = useStore(
    useShallow(state => ({
      showError: state.showError,
    }))
  );

  const [roles, setRoles] = useState<RoleResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingRole, setEditingRole] = useState<RoleResponse | null>(null);
  const [bulkAssignRole, setBulkAssignRole] = useState<RoleResponse | null>(
    null
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const rolesRes = await listRoles({
        query: { limit: 500 },
      });
      setRoles(rolesRes.data ?? []);
    } catch (error) {
      console.error('Failed to load roles:', error);
      showError(
        'Failed to Load Roles',
        'Unable to load roles. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card>
      <CardHeader className='flex flex-row items-start justify-between'>
        <div>
          <CardTitle>Roles</CardTitle>
          <CardDescription>
            All roles available in your agency, including system-defined and
            custom roles.
          </CardDescription>
        </div>
        <CreateRoleButton onCreated={load} existingRoles={roles} />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='flex items-center justify-center py-10'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            <span className='ml-2 text-sm text-muted-foreground'>
              Loading roles...
            </span>
          </div>
        ) : roles.length === 0 ? (
          <p className='text-sm text-muted-foreground py-6 text-center'>
            No roles found.
          </p>
        ) : (
          <div className='rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow className='hover:bg-transparent'>
                  <TableHead className='w-48'>Role</TableHead>
                  <TableHead className='w-24'>Scope</TableHead>
                  <TableHead className='w-28'>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead className='w-24' />
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map(role => {
                  const permissions = role.permissions ?? [];
                  return (
                    <TableRow
                      key={role.id}
                      className='even:bg-muted/50 odd:hover:bg-transparent even:hover:bg-muted/50'
                    >
                      <TableCell className='font-medium'>{role.name}</TableCell>
                      <TableCell>
                        <Badge variant='outline' className='capitalize'>
                          {role.scope.toLowerCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {role.is_system ? (
                          <Badge variant='secondary'>System</Badge>
                        ) : (
                          <Badge variant='outline'>Custom</Badge>
                        )}
                      </TableCell>
                      <TableCell className='text-sm text-muted-foreground'>
                        {role.description ?? (
                          <span className='italic'>No description</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {permissions.length === 0 ? (
                          <span className='text-sm text-muted-foreground italic'>
                            None
                          </span>
                        ) : (
                          <TooltipProvider delayDuration={150}>
                            <div className='flex flex-wrap gap-1'>
                              {permissions.map(perm => (
                                <PermissionBadge
                                  key={perm.id}
                                  permission={perm}
                                  variant='outline'
                                />
                              ))}
                            </div>
                          </TooltipProvider>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center gap-1'>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => setBulkAssignRole(role)}
                            aria-label={`Bulk assign users to ${role.name}`}
                          >
                            <UserPlus className='h-4 w-4' />
                          </Button>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => setEditingRole(role)}
                            aria-label={`Edit ${role.name}`}
                          >
                            <Pencil className='h-4 w-4' />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {editingRole && (
        <EditRoleDialog
          role={editingRole}
          open={editingRole !== null}
          onClose={() => setEditingRole(null)}
          onUpdated={load}
        />
      )}

      {bulkAssignRole && (
        <BulkAssignRoleDialog
          role={bulkAssignRole}
          open={bulkAssignRole !== null}
          onClose={() => setBulkAssignRole(null)}
        />
      )}
    </Card>
  );
};

export default RolesTable;
